import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInvoiceApiV1 } from '../../clients/quipli-service/invoice/invoice-api-v1';
import { Invoice, InvoiceSearchSummaryData, SearchInvoiceRequest } from '../../clients/quipli-service/invoice/invoice';
import { Location } from '../../clients/quipli-service/location/location';
import { DEFAULT_INVOICE_TABLE_COLUMNS, InvoiceList } from './invoice-list';
import { Card } from '../../components/card/card';
import { displayMoneyFormat } from '../../util/util';
import './collectibles.scss';
import { Paginator } from '../../components/paginator/paginator-v2';
import { Pagination } from '../../clients/quipli-service/pagination/pagination';
import { CollectionsFilters } from './components/collectibles-filters';
import { DateTime } from 'luxon';
import FileSaver from 'file-saver';
import { Button } from '../../components/form/button';
import Icon from '../../components/icons/icon';
import useRequestQueryParams from '../../hooks/use-request-query-params';
import { SpinnerModal } from '../../components/modal/spinner-modal';
import { Spinner } from '../../components/spinner/spinner';
import { toast } from '../../util/toasts';

function extractInvoiceSearchParams(params: URLSearchParams): SearchInvoiceRequest {
  return {
    query: params.has('query') ? params.get('query') : undefined,
    date: {
      from: params.get('dateFrom') ? DateTime.fromISO(params.get('dateFrom')!) : undefined,
      to: params.get('dateTo') ? DateTime.fromISO(params.get('dateTo')!) : undefined,
    },
    dueDate: {
      from: params.get('dueDateFrom') ? DateTime.fromISO(params.get('dueDateFrom')!) : undefined,
      to: params.get('dueDateTo') ? DateTime.fromISO(params.get('dueDateTo')!) : undefined,
    },
    daysPastDue: params.get('daysPastDue') ? parseInt(params.get('daysPastDue')!) : undefined,
    locationId: params.get('locationId') ? parseInt(params.get('locationId')!) : undefined,
    invoiceStatusFilter: params.get('invoiceStatusFilter')?.split(',') || undefined,
    workflowStatusFilter: params.get('workflowStatusFilter')?.split(',') || undefined,
    onAccountStatus: params.get('onAccountStatus') || undefined,
    sortBy: params.get('sortBy') || 'dueDate',
    sortDirection: params.get('sortDirection') || 'desc',
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
    pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
  } as SearchInvoiceRequest;
}

export const Collectibles = () => {
  const { getMultipleConfigurations, listLocations } = useAPI();
  const { searchInvoices, searchSummaryData, exportInvoices } = useInvoiceApiV1();
  const [currency, setCurrency] = useState<string>();
  const [displayTaxByJurisdiction, setDisplayTaxByJurisdiction] = useState<boolean>();
  const [taxInclusive, setTaxInclusive] = useState<boolean>(false);
  const [taxLabel, setTaxLabel] = useState<string>('Tax');
  const [invoices, setInvoices] = useState<Pagination<Invoice>>();
  const [locations, setLocations] = useState<Location[]>();
  const [summaryData, setSummaryData] = useState<InvoiceSearchSummaryData>();
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // List searchRequest hooks
  const [params] = useSearchParams();
  const {
    request: searchRequest,
    setRequest: setSearchRequest,
    numOfParams: activeFilters,
  } = useRequestQueryParams<SearchInvoiceRequest>(extractInvoiceSearchParams(params), () => refreshInvoices());

  async function refreshInvoices(sortBy?: 'date' | 'dueDate' | 'invoiceNumber') {
    try {
      setIsLoading(true);
      if (sortBy) {
        setSearchRequest((req) => {
          let sortDirection: 'asc' | 'desc' = 'asc';
          if (req.sortBy === sortBy) {
            sortDirection = req.sortDirection === 'asc' ? 'desc' : 'asc';
          }
          return {
            ...req,
            sortBy: sortBy || req.sortBy,
            sortDirection,
          };
        });
      } else if (searchRequest.pageSize) {
        await searchInvoices(searchRequest).then((res) => setInvoices(res));
        await searchSummaryData(searchRequest).then((res) => setSummaryData(res));
      }
    } catch {
      toast('Failed to load collectibles', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!currency || displayTaxByJurisdiction === undefined) {
      getMultipleConfigurations([
        'CONF_CURRENCY',
        'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN',
        'CONF_SHOW_PRICES_WITH_TAX',
        'CONF_PRICE_TAX_LABEL',
      ]).then((confs) => {
        setCurrency(confs.find((c) => c.variable === 'CONF_CURRENCY')?.value || 'USD');
        setDisplayTaxByJurisdiction(
          confs.find((c) => c.variable === 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN')?.value === '1',
        );
        setTaxInclusive(confs.find((config) => config.variable == 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1');
        setTaxLabel(confs.find((config) => config.variable == 'CONF_PRICE_TAX_LABEL')?.value || 'Tax');
      });
    }
    if (!locations) {
      listLocations().then(setLocations);
    }
  }, []);

  const doExport = async () => {
    setIsExporting(true);
    try {
      const { pageSize, pageNumber, ...req } = searchRequest;
      const file = await exportInvoices(req);

      const exportFileName = `Collectibles_${DateTime.local({
        zone: locations?.find(() => true)?.timezone,
      }).toISODate()}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e) {
      console.error(e);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="collectibles flex flex-column gap-4">
      <h2>Collectibles</h2>
      <div className="flex justify-between">
        <CollectionsFilters
          locations={locations || []}
          searchRequest={searchRequest}
          setSearchRequest={setSearchRequest}
          activeFilters={activeFilters}
        />
        <Button className="button-yellow h-[46px]" onClick={() => doExport()} data-cy="export">
          Export
          <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
        </Button>
      </div>
      <div className="summary-row">
        <Card className="flex flex-column grow">
          <span>Past Due</span>
          <span>{displayMoneyFormat(summaryData?.pastDue, currency)}</span>
        </Card>
        <Card className="flex flex-column grow">
          <span>Payable</span>
          <span>{displayMoneyFormat(summaryData?.payable, currency)}</span>
        </Card>
        <Card className="flex flex-column grow">
          <span>Received</span>
          <span>{displayMoneyFormat(summaryData?.received, currency)}</span>
        </Card>
      </div>
      <Card>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            {invoices && currency && displayTaxByJurisdiction !== undefined && (
              <InvoiceList
                invoices={invoices.items}
                locations={locations}
                currency={currency}
                refresh={refreshInvoices}
                sortable={true}
                sort={
                  searchRequest.sortBy &&
                  searchRequest.sortDirection && {
                    field: searchRequest.sortBy,
                    direction: searchRequest.sortDirection,
                  }
                }
                enabledColumns={[...DEFAULT_INVOICE_TABLE_COLUMNS, 'DaysPastDue']}
                displayTaxByJurisdiction={displayTaxByJurisdiction}
                taxInclusive={taxInclusive}
                taxLabel={taxLabel}
              />
            )}
          </>
        )}
        <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Collectibles'} />
        <Paginator
          pagination={invoices?.pagination}
          request={searchRequest}
          setRequest={(pag) =>
            setSearchRequest((req) => {
              return { ...req, ...pag };
            })
          }
        />
      </Card>
    </div>
  );
};
